import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { eventSessionInvitationRepository } from "@/service/repository/eventSessionInvitationRepository";
import store from "@/store";

export const useUserEventRegistrationsStore = defineStore('userEventRegistrations', () => {
	// State
	const registrations = ref([]);
	const isLoading = ref(false);
	const error = ref(null);

	// Getters
	const hasRegistrations = computed(() => registrations.value.length > 0);

	// Returns the server logical date ("YYYY-MM-DD") for comparisons, falling back to
	// the client's local date if the config hasn't loaded yet.
	function getLogicalDate() {
		return store.state.config.logicalDate
			?? new Date().toISOString().slice(0, 10);
	}

	const upcomingRegistrations = computed(() => {
		const today = getLogicalDate();
		return registrations.value.filter(reg => {
			if (reg.status === 'cancelled') return false;
			return (reg.session?.businessDate ?? '') >= today;
		}).sort((a, b) => {
			return (a.session.businessDate > b.session.businessDate) ? 1 : -1;
		});
	});

	// Only cancelled registrations whose event date is on or after today — purely
	// historical cancellations are not useful to display.
	const cancelledRegistrations = computed(() => {
		const today = getLogicalDate();
		return registrations.value
			.filter(reg => reg.status === 'cancelled' && (reg.session?.businessDate ?? '') >= today)
			.sort((a, b) => {
				const da = a.session?.businessDate ?? '';
				const db = b.session?.businessDate ?? '';
				return db > da ? 1 : -1; // most recent first
			});
	});

	const pastRegistrations = computed(() => {
		const today = getLogicalDate();
		return registrations.value.filter(reg => {
			return (reg.session?.businessDate ?? '') < today;
		}).sort((a, b) => {
			const da = a.session?.businessDate ?? '';
			const db = b.session?.businessDate ?? '';
			return db > da ? 1 : -1; // most recent first
		});
	});

	// Actions
	function clearRegistrations() {
		registrations.value = [];
		error.value = null;
	}

	async function loadRegistrations(hostCode) {
		if (!hostCode) {
			error.value = 'No se proporcionó código de usuario.';
			return false;
		}

		isLoading.value = true;
		error.value = null;

		try {
			const response = await eventSessionInvitationRepository.getUserEventRegistrations(hostCode);

			if (response.result === 0) {
				registrations.value = response.registrations || [];
				return true;
			} else {
				error.value = 'Error al cargar los registros de eventos.';
				return false;
			}
		} catch (err) {
			console.error('Error loading event registrations:', err);
			error.value = 'Error de conexión.';
			return false;
		} finally {
			isLoading.value = false;
		}
	}

	return {
		// State
		registrations,
		isLoading,
		error,
		// Getters
		hasRegistrations,
		upcomingRegistrations,
		cancelledRegistrations,
		pastRegistrations,
		// Actions
		clearRegistrations,
		loadRegistrations,
	};
});

